/**
 * The pure model of a link's validity window (1.J stage 3, deliverables 24 and 25). A link holds at
 * a moment that lies in its span (`lo` inclusive, `hi` exclusive, null unbounded). Spans, ticks
 * and ranks are the server's; nothing here computes one or refuses a save.
 */
import type { CompiledCalendar } from '@codex/calendar';
import {
  ApiError,
  type LinkBoundaryV1,
  type LinkUpdateV1,
  type LinkV1,
  type PlacementV1,
  type TickSpanV1,
} from '@codex/contracts';
import { linkRows } from '../../when/chronology-model.js';
import { INVALID_SPAN } from '../../when/words.js';
import { noticeFor } from '../messages.js';

export type NowState = 'none' | 'invalid' | 'unknown' | 'holds' | 'maybe' | 'ended' | 'future';

export const NOW_WORDS = {
  holds: 'Holds now',
  maybe: 'May hold now',
  ended: 'Ended',
  future: 'Not yet',
  invalid: INVALID_SPAN,
} as const;

export const BOUNDARY_REFUSED_TEXT = "That date can't bound this link.";

type Validity = Pick<LinkV1, 'validFrom' | 'validTo' | 'ongoing'>;
type Windowed = Validity & Pick<LinkV1, 'certainSpan' | 'possibleSpan' | 'spanStatus'>;

/** A boundary on either side, or no end yet. */
export function hasValidity(link: Validity): boolean {
  return link.validFrom !== null || link.validTo !== null || link.ongoing;
}

function spanHolds(span: TickSpanV1, tick: number): boolean {
  if (span.empty) return false;
  return (span.lo === null || span.lo <= tick) && (span.hi === null || tick < span.hi);
}

/** Where NOW stands against the link's window. */
export function nowState(link: Windowed, now: number | null): NowState {
  if (!hasValidity(link)) return 'none';
  if (link.spanStatus === 'invalid' || link.possibleSpan.empty) return 'invalid';
  if (now === null) return 'unknown';
  if (spanHolds(link.certainSpan, now)) return 'holds';
  if (spanHolds(link.possibleSpan, now)) return 'maybe';
  const { lo, hi } = link.possibleSpan;
  if (hi !== null && hi <= now) return 'ended';
  if (lo !== null && lo > now) return 'future';
  return 'unknown';
}

/** The window in 1.O-1's words, so the Chronology tab and this package read alike; null without a boundary. */
export function windowText(
  link: LinkV1,
  docId: string,
  titles: ReadonlyMap<string, string>,
  calendar: CompiledCalendar,
): string | null {
  return linkRows([link], docId, titles, calendar)[0]?.text ?? null;
}

/** One side's words ("From …", "Until …"), "Not set" when that side is null. */
export function sideText(
  link: LinkV1,
  side: 'from' | 'to',
  docId: string,
  titles: ReadonlyMap<string, string>,
  calendar: CompiledCalendar,
): string {
  if ((side === 'from' ? link.validFrom : link.validTo) === null) return 'Not set';
  const alone: LinkV1 = {
    ...link,
    validFrom: side === 'from' ? link.validFrom : null,
    validTo: side === 'to' ? link.validTo : null,
    ongoing: false,
    spanStatus: 'ok',
  };
  return windowText(alone, docId, titles, calendar) ?? 'Not set';
}

/** The NOW word, except where the window already says the dates cannot be worked out. */
export function stateShown(
  link: Windowed,
  now: number | null,
  window: string | null,
): string | null {
  const state = nowState(link, now);
  if (state === 'none' || state === 'unknown') return null;
  const word = NOW_WORDS[state];
  return window === INVALID_SPAN && word === INVALID_SPAN ? null : word;
}

/** The target's own date as a range: its span, else its bounds, else unknown. */
export function targetRange(placement: PlacementV1 | null): TickSpanV1 | null {
  if (placement === null) return null;
  if (!placement.span.empty) return placement.span;
  if (placement.boundLo !== null || placement.boundHi !== null) {
    return { empty: false, lo: placement.boundLo, hi: placement.boundHi };
  }
  return null;
}

function intersects(a: { lo: number | null; hi: number | null }, b: TickSpanV1): boolean {
  if (b.empty) return false;
  const aReachesB = a.lo === null || b.hi === null || a.lo < b.hi;
  const bReachesA = b.lo === null || a.hi === null || b.lo < a.hi;
  return aReachesB && bReachesA;
}

/** True when the window and the page's own date are both known and do not overlap. */
export function datesDisagree(
  link: Validity & Pick<LinkV1, 'possibleSpan'>,
  range: TickSpanV1 | null,
): boolean {
  if (!hasValidity(link) || link.possibleSpan.empty || range === null || range.empty) return false;
  return !intersects(link.possibleSpan, range);
}

export interface ValidityDraft {
  readonly validFrom: LinkBoundaryV1 | null;
  readonly validTo: LinkBoundaryV1 | null;
  readonly ongoing: boolean;
}

export type ValidityPatch = Pick<LinkUpdateV1, 'validFrom' | 'validTo' | 'ongoing'>;

const same = (a: LinkBoundaryV1 | null, b: LinkBoundaryV1 | null) =>
  JSON.stringify(a) === JSON.stringify(b);

/** Only the keys the draft changed; null when nothing did. */
export function validityPatch(link: Validity, draft: ValidityDraft): ValidityPatch | null {
  const patch: { -readonly [K in keyof ValidityPatch]: ValidityPatch[K] } = {};
  if (!same(draft.validFrom, link.validFrom)) patch.validFrom = draft.validFrom;
  if (draft.ongoing) {
    if (!link.ongoing) {
      patch.validTo = null;
      patch.ongoing = true;
    }
  } else {
    if (!same(draft.validTo, link.validTo)) patch.validTo = draft.validTo;
    if (link.ongoing) patch.ongoing = false;
  }
  return Object.keys(patch).length === 0 ? null : patch;
}

export interface BoundaryRefusal {
  readonly side: 'from' | 'to' | null;
  readonly text: string;
}

/** A 422 that names a boundary gives its side; anything else the fixed notice of its code. */
export function boundaryRefusal(error: unknown): BoundaryRefusal {
  if (error instanceof ApiError && error.status === 422 && error.code === 'invalid_body') {
    const path = (error.body.details as { path?: unknown } | undefined)?.path;
    const first = Array.isArray(path) ? path[0] : undefined;
    if (first === 'validFrom') return { side: 'from', text: BOUNDARY_REFUSED_TEXT };
    if (first === 'validTo') return { side: 'to', text: BOUNDARY_REFUSED_TEXT };
  }
  return { side: null, text: noticeFor(error instanceof ApiError ? error.code : 'network') };
}
